import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';

const STAGES = [
  "Interpreting your idea",
  "Exploring layouts",
  "Composing template",
  "Refining visuals"
];

// SVG Paths for the wireframe
const WIREFRAME_PATHS = {
  header: "M 0 0 L 300 0 L 300 40 L 0 40 Z",
  heroImage: "M 160 60 L 280 60 L 280 160 L 160 160 Z",
  heroTitle: "M 20 70 L 140 70",
  heroSubtitle: "M 20 90 L 120 90",
  heroButton: "M 20 120 L 100 120 L 100 150 L 20 150 Z",
  card1: "M 20 190 L 93 190 L 93 260 L 20 260 Z",
  card2: "M 113 190 L 186 190 L 186 260 L 113 260 Z",
  card3: "M 206 190 L 280 190 L 280 260 L 206 260 Z",
};

const CursorIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-xl">
    <path d="M5.65376 12.3673H5.46026L5.31717 12.4976L0.500002 16.8829L0.500002 1.19177L11.7841 12.3673H5.65376Z" fill="#101928" stroke="white" strokeWidth="1.5" />
  </svg>
);

const ColorfulBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none select-none bg-indigo-50/30">
    {/* Gradient Mesh Layers */}
    <motion.div
      className="absolute top-[-20%] left-[-20%] w-[70%] h-[70%] rounded-full bg-gradient-to-r from-blue-200/40 to-cyan-200/40 blur-[100px]"
      animate={{
        x: [0, 50, -20, 0],
        y: [0, 30, -50, 0],
        scale: [1, 1.1, 0.9, 1]
      }}
      transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute top-[10%] right-[-10%] w-[60%] h-[60%] rounded-full bg-gradient-to-l from-purple-200/40 to-pink-200/40 blur-[100px]"
      animate={{
        x: [0, -40, 30, 0],
        y: [0, 60, 20, 0],
        scale: [1, 1.2, 1, 1]
      }}
      transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute bottom-[-20%] left-[20%] w-[60%] h-[60%] rounded-full bg-gradient-to-t from-amber-100/50 to-orange-100/50 blur-[90px]"
      animate={{
        x: [0, 30, -40, 0],
        y: [0, -20, 30, 0],
        rotate: [0, 10, -10, 0]
      }}
      transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute bottom-[10%] right-[30%] w-[40%] h-[40%] rounded-full bg-emerald-100/30 blur-[80px]"
      animate={{ scale: [1, 1.3, 1] }}
      transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
    />

    {/* Grain Texture Overlay */}
    <div className="absolute inset-0 opacity-30 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] mix-blend-overlay brightness-100 contrast-150" />
  </div>
);

const ParallaxArtifacts = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {/* Floating Icons/Shapes */}
    <motion.div
      className="absolute top-[15%] left-[10%] w-12 h-12 bg-white/40 backdrop-blur-md rounded-xl border border-white/60 flex items-center justify-center shadow-lg shadow-purple-500/5"
      animate={{ y: [0, -20, 0], rotate: [0, 10, 0] }}
      transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
    >
      <div className="w-6 h-6 rounded-md bg-purple-200/50" />
    </motion.div>

    <motion.div
      className="absolute bottom-[20%] right-[10%] w-16 h-16 bg-white/40 backdrop-blur-md rounded-full border border-white/60 flex items-center justify-center shadow-lg shadow-blue-500/5"
      animate={{ y: [0, 30, 0], scale: [1, 1.05, 1] }}
      transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
    >
      <div className="w-8 h-8 rounded-full bg-blue-200/50" />
    </motion.div>

    {/* Abstract UI Fragments */}
    <motion.div
      className="absolute top-[30%] right-[20%] w-32 h-20 bg-white/20 backdrop-blur-[2px] border border-white/40 rounded-lg -z-10"
      animate={{ y: [0, -15, 0], rotate: [0, -5, 0] }}
      transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
    />

    <motion.div
      className="absolute bottom-[30%] left-[15%] w-40 h-24 bg-white/20 backdrop-blur-[2px] border border-white/40 rounded-lg -z-10"
      animate={{ y: [0, 25, 0], rotate: [0, 3, 0] }}
      transition={{ duration: 14, repeat: Infinity, ease: "easeInOut", delay: 2 }}
    />
  </div>
);

export const AiLoader = () => {
  const [currentStage, setCurrentStage] = useState(0);

  useEffect(() => {
    // 9 seconds total / 4 stages = 2.25s per stage
    const interval = setInterval(() => {
      setCurrentStage(prev => (prev < STAGES.length - 1 ? prev + 1 : prev));
    }, 2250);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-white text-[#101928] cursor-wait overflow-hidden font-sans">
      {/* Background Layers */}
      <ColorfulBackground />
      <ParallaxArtifacts />

      <div className="relative z-10 flex flex-col items-center gap-10">
        {/* Main Composition Canvas (Artboard) */}
        <motion.div
          className="relative w-[340px] h-[320px] bg-white/80 backdrop-blur-xl shadow-2xl shadow-indigo-200/40 rounded-2xl border border-white/80 overflow-hidden"
          initial={{ opacity: 0, scale: 0.9, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          {/* Header Bar */}
          <div className="absolute top-0 left-0 right-0 h-8 bg-gray-50/50 border-b border-gray-100 flex items-center px-3 gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400/20" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/20" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-400/20" />
            <div className="ml-auto text-[9px] font-mono text-gray-400">TEMPLATE_V1</div>
          </div>

          {/* Internal Canvas Padding */}
          <div className="absolute inset-0 pt-8 p-5">

            {/* Snap/Alignment Guides */}
            <AnimatePresence>
              {currentStage === 1 && (
                <>
                  <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="absolute top-0 bottom-0 left-[20px] w-px bg-cyan-400/40 z-20"
                  />
                  <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="absolute top-[190px] left-0 right-0 h-px bg-cyan-400/40 z-20"
                  />
                </>
              )}
            </AnimatePresence>

            {/* Drawing Layer (Wireframes) */}
            <svg className="absolute inset-0 w-full h-full overflow-visible z-10 pointer-events-none">
              {Object.entries(WIREFRAME_PATHS).map(([key, d], i) => (
                <motion.path
                  key={key}
                  d={d}
                  fill="transparent"
                  stroke={currentStage >= 2 ? "rgba(0,0,0,0)" : "#94a3b8"}
                  strokeWidth="1.5"
                  strokeDasharray={currentStage === 0 ? "4 4" : "0 0"}
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{
                    pathLength: currentStage >= 1 ? 1 : 0,
                    opacity: currentStage >= 1 ? 1 : 0,
                  }}
                  transition={{
                    duration: 1.2,
                    delay: currentStage === 1 ? i * 0.12 : 0,
                    ease: "easeInOut"
                  }}
                />
              ))}
            </svg>

            {/* Components Filling Layer */}
            <div className="absolute inset-0 z-0 pt-8 p-5">
              {/* Header */}
              <motion.div
                className="absolute top-0 left-0 w-full h-[40px] bg-white border-b border-gray-100"
                initial={{ opacity: 0 }}
                animate={{ opacity: currentStage >= 2 ? 1 : 0 }}
                transition={{ duration: 0.5 }}
              />

              {/* Hero Image Block */}
              <motion.div
                className="absolute top-[60px] right-[20px] w-[120px] h-[100px] bg-blue-50 rounded-lg overflow-hidden border border-blue-100"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{
                  scale: currentStage >= 2 ? 1 : 0.8,
                  opacity: currentStage >= 2 ? 1 : 0,
                  backgroundColor: currentStage >= 3 ? "#EFF6FF" : "#F8FAFC", // blue-50 -> slate-50
                }}
                transition={{ duration: 0.6, delay: 0.2, type: "spring", bounce: 0.3 }}
              >
                {/* Image Placeholder */}
                <motion.div
                  className="absolute inset-0 flex items-center justify-center text-blue-200"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: currentStage >= 3 ? 1 : 0 }}
                >
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                </motion.div>
              </motion.div>

              {/* Text Lines */}
              <motion.div
                className="absolute top-[70px] left-[20px] h-[16px] w-[120px] bg-gray-200 rounded-md"
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: currentStage >= 2 ? 120 : 0, opacity: currentStage >= 2 ? 1 : 0 }}
              />
              <motion.div
                className="absolute top-[96px] left-[20px] h-[10px] w-[100px] bg-gray-100 rounded-sm"
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: currentStage >= 2 ? 100 : 0, opacity: currentStage >= 2 ? 1 : 0 }}
                transition={{ delay: 0.1 }}
              />

              {/* Button */}
              <motion.div
                className="absolute top-[120px] left-[20px] h-[36px] w-[90px] bg-[#101928] rounded-lg text-white flex items-center justify-center shadow-lg shadow-gray-300/50"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{
                  opacity: currentStage >= 2 ? 1 : 0,
                  scale: currentStage >= 2 ? 1 : 0.5,
                  backgroundColor: currentStage >= 3 ? "#0F172A" : "#334155"
                }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200, damping: 20 }}
              >
                <span className="text-[10px] font-medium tracking-wide">Generate</span>
              </motion.div>

              {/* Cards Grid */}
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={`card-${i}`}
                  className="absolute top-[190px] h-[70px] w-[73px] bg-white border border-gray-100 rounded-lg"
                  style={{ left: 20 + (i * 93) }}
                  initial={{ y: 30, opacity: 0 }}
                  animate={{
                    y: currentStage >= 2 ? 0 : 30,
                    opacity: currentStage >= 2 ? 1 : 0,
                    boxShadow: currentStage >= 3 ? "0 4px 12px -2px rgba(0, 0, 0, 0.08)" : "none",
                    borderColor: currentStage >= 3 ? "#E2E8F0" : "#F1F5F9"
                  }}
                  transition={{ delay: 0.4 + (i * 0.1), type: "spring" }}
                >
                  {/* Card skeleton content */}
                  <div className="w-full h-1/2 bg-gray-50 rounded-t-lg" />
                  <div className="p-1.5 space-y-1">
                    <div className="w-2/3 h-1.5 bg-gray-100 rounded-full" />
                    <div className="w-1/2 h-1.5 bg-gray-50 rounded-full" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Animated Tool/Cursor */}
          <motion.div
            className="absolute z-50 pointer-events-none"
            initial={{ x: 380, y: 360, opacity: 0 }}
            animate={{
              x: [380, 180, 70, 270, 90, 380],
              y: [360, 50, 110, 130, 165, 360],
              opacity: [0, 1, 1, 1, 1, 0]
            }}
            transition={{
              duration: 4.5,
              times: [0, 0.15, 0.35, 0.55, 0.75, 1],
              ease: "easeInOut",
              repeat: Infinity,
              repeatDelay: 0.5
            }}
          >
            <CursorIcon />
            {/* Contextual Action Label */}
            <motion.div
              className="absolute left-4 top-4 bg-white/90 backdrop-blur text-[#101928] border border-gray-200 text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap tracking-wide font-medium shadow-md"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 1 }}
            >
              Composing...
            </motion.div>
          </motion.div>

        </motion.div>

        {/* Status Text Area */}
        <div className="h-20 flex flex-col items-center justify-center min-w-[320px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStage}
              initial={{ opacity: 0, y: 10, filter: "blur(4px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -10, filter: "blur(4px)" }}
              transition={{ duration: 0.5 }}
              className="flex flex-col items-center gap-3"
            >
              <h2 className="text-[#101928] text-lg font-medium tracking-tight bg-white/50 px-4 py-1 rounded-full backdrop-blur-sm">
                {STAGES[currentStage]}
              </h2>
              {/* Loader Dots */}
              <div className="flex gap-1.5">
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    className="w-1.5 h-1.5 rounded-full bg-[#101928]"
                    animate={{ opacity: [0.3, 1, 0.3], scale: [1, 1.2, 1] }}
                    transition={{ duration: 1, delay: i * 0.2, repeat: Infinity }}
                  />
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

